import { useState, useCallback, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Trash2, Minus, Plus, ArrowRight, Package, CreditCard, Banknote, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useCart } from '@/context/CartContext';
import { useCurrency } from '@/context/CurrencyContext';
import { toast } from 'sonner';
import axios from 'axios';
import CourierSelection from '@/components/CourierSelection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Paystack countries
const PAYSTACK_COUNTRIES = ['NG', 'GH', 'ZA', 'KE'];

// Empty Cart Component
const EmptyCart = () => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
    <div className="text-center">
      <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-6">
        <ShoppingCart className="w-10 h-10 text-muted-foreground" />
      </div>
      <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Archivo, sans-serif' }}>
        Your cart is empty
      </h1>
      <p className="text-muted-foreground mb-8">
        Looks like you haven&apos;t added any items yet
      </p>
      <Link to="/products" data-testid="continue-shopping-empty">
        <Button size="lg" className="gap-2">
          <Package className="w-5 h-5" />
          Continue Shopping
        </Button>
      </Link>
    </div>
  </div>
);

// Quantity Controls Component
const QuantityControls = ({ item, itemKey, onDecrease, onIncrease, disabled }) => (
  <div className="flex items-center border rounded-md">
    <Button 
      variant="ghost" 
      size="icon"
      className="h-8 w-8"
      onClick={onDecrease}
      disabled={disabled}
      data-testid={`decrease-${itemKey}`}
    >
      <Minus className="w-3 h-3" />
    </Button>
    <span className="w-10 text-center text-sm font-medium">{item.quantity}</span>
    <Button 
      variant="ghost" 
      size="icon"
      className="h-8 w-8"
      onClick={onIncrease}
      disabled={disabled}
      data-testid={`increase-${itemKey}`}
    >
      <Plus className="w-3 h-3" />
    </Button>
  </div>
);

// Cart Item Component
const CartItem = ({ item, itemKey, onQuantityChange, onRemove, loading }) => (
  <Card data-testid={`cart-item-${itemKey}`}>
    <CardContent className="p-4">
      <div className="flex gap-4">
        <Link to={`/products/${item.product_id}`} className="shrink-0">
          <div className="w-24 h-24 rounded-md overflow-hidden bg-muted">
            <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
          </div>
        </Link>
        
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start">
            <div>
              <Link to={`/products/${item.product_id}`}>
                <h3 className="font-semibold hover:text-foreground/80 transition-colors" style={{ fontFamily: 'Archivo, sans-serif' }}>
                  {item.name}
                </h3>
              </Link>
              {/* Show selected variants */}
              {(item.selected_size || item.selected_color) && (
                <div className="flex gap-2 mt-1">
                  {item.selected_size && (
                    <Badge variant="secondary" className="text-xs">
                      Size: {item.selected_size}
                    </Badge>
                  )}
                  {item.selected_color && (
                    <Badge variant="secondary" className="text-xs">
                      Color: {item.selected_color}
                    </Badge>
                  )}
                </div>
              )}
              <p className="text-sm text-muted-foreground mt-1">${item.price.toFixed(2)} each</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="text-muted-foreground hover:text-destructive"
              onClick={() => onRemove(itemKey)}
              data-testid={`remove-item-${itemKey}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="flex items-center justify-between mt-4">
            <QuantityControls 
              item={item}
              itemKey={itemKey}
              onDecrease={() => onQuantityChange(itemKey, item.quantity - 1)}
              onIncrease={() => onQuantityChange(itemKey, item.quantity + 1)}
              disabled={loading}
            />
            <span className="font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              ${(item.price * item.quantity).toFixed(2)}
            </span>
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Order Summary Component
const OrderSummary = ({ total, shippingCost = 0, taxInfo = null }) => (
  <div className="space-y-3">
    <div className="flex justify-between text-sm">
      <span className="text-muted-foreground">Subtotal</span>
      <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>${total.toFixed(2)}</span>
    </div>
    <div className="flex justify-between text-sm">
      <span className="text-muted-foreground flex items-center gap-1">
        <Truck className="w-3 h-3" />
        Shipping
      </span>
      {shippingCost > 0 ? (
        <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>${shippingCost.toFixed(2)}</span>
      ) : (
        <span className="text-muted-foreground text-xs">Select delivery option</span>
      )}
    </div>
    {taxInfo && taxInfo.tax_amount > 0 && (
      <div className="flex justify-between text-sm">
        <span className="text-muted-foreground flex items-center gap-1">
          {taxInfo.tax_name} ({taxInfo.tax_rate}%{taxInfo.tax_included ? ' incl.' : ''})
        </span>
        <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          {taxInfo.tax_included ? 'Included' : `$${taxInfo.tax_amount.toFixed(2)}`}
        </span>
      </div>
    )}
    <Separator />
    <div className="flex justify-between font-semibold text-lg">
      <span>Total</span>
      <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>
        ${taxInfo ? taxInfo.total.toFixed(2) : (total + shippingCost).toFixed(2)}
      </span>
    </div>
    {taxInfo && taxInfo.tax_included && taxInfo.tax_amount > 0 && (
      <p className="text-xs text-muted-foreground text-right">
        (Includes ${taxInfo.tax_amount.toFixed(2)} {taxInfo.tax_name})
      </p>
    )}
  </div>
);

// Customer Form Fields Component
const CustomerFormFields = ({ customerInfo, onChange }) => (
  <>
    <div className="space-y-2">
      <Label htmlFor="email">Email *</Label>
      <Input
        id="email"
        type="email"
        placeholder="your@email.com"
        value={customerInfo.email}
        onChange={(e) => onChange('email', e.target.value)}
        required
        data-testid="checkout-email"
      />
    </div>
    
    <div className="space-y-2">
      <Label htmlFor="name">Full Name *</Label>
      <Input
        id="name"
        type="text"
        placeholder="John Doe"
        value={customerInfo.name}
        onChange={(e) => onChange('name', e.target.value)}
        required
        data-testid="checkout-name"
      />
    </div>

    <div className="space-y-2">
      <Label htmlFor="phone">Phone Number *</Label>
      <Input
        id="phone"
        type="tel"
        placeholder="+34 612 345 678"
        value={customerInfo.phone}
        onChange={(e) => onChange('phone', e.target.value)}
        required
        data-testid="checkout-phone"
      />
      <p className="text-xs text-muted-foreground">Required for shipping updates</p>
    </div>
    
    <div className="space-y-2">
      <Label htmlFor="address">Shipping Address *</Label>
      <Input
        id="address"
        type="text"
        placeholder="Street address, apt/unit"
        value={customerInfo.address}
        onChange={(e) => onChange('address', e.target.value)}
        required
        data-testid="checkout-address"
      />
    </div>

    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-2">
        <Label htmlFor="city">City *</Label>
        <Input
          id="city"
          type="text"
          placeholder="Barcelona"
          value={customerInfo.city}
          onChange={(e) => onChange('city', e.target.value)}
          required
          data-testid="checkout-city"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="postalCode">Postal Code *</Label>
        <Input
          id="postalCode"
          type="text"
          placeholder="08001"
          value={customerInfo.postalCode}
          onChange={(e) => onChange('postalCode', e.target.value)}
          required
          data-testid="checkout-postal"
        />
      </div>
    </div>

    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-2">
        <Label htmlFor="province">State/Province</Label>
        <Input
          id="province"
          type="text"
          placeholder="Cataluña"
          value={customerInfo.province}
          onChange={(e) => onChange('province', e.target.value)}
          data-testid="checkout-province"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="country">Country *</Label>
        <select
          id="country"
          value={customerInfo.country}
          onChange={(e) => onChange('country', e.target.value)}
          className="w-full h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm"
          required
          data-testid="checkout-country"
        >
          <option value="">Select country</option>
          <optgroup label="Europe">
            <option value="ES">🇪🇸 Spain</option>
            <option value="DE">🇩🇪 Germany</option>
            <option value="FR">🇫🇷 France</option>
            <option value="IT">🇮🇹 Italy</option>
            <option value="PT">🇵🇹 Portugal</option>
            <option value="NL">🇳🇱 Netherlands</option>
            <option value="GB">🇬🇧 UK</option>
            <option value="BE">🇧🇪 Belgium</option>
            <option value="PL">🇵🇱 Poland</option>
          </optgroup>
          <optgroup label="Africa">
            <option value="NG">🇳🇬 Nigeria</option>
            <option value="ZA">🇿🇦 South Africa</option>
            <option value="EG">🇪🇬 Egypt</option>
            <option value="KE">🇰🇪 Kenya</option>
            <option value="GH">🇬🇭 Ghana</option>
          </optgroup>
          <optgroup label="Americas">
            <option value="US">🇺🇸 USA</option>
            <option value="CA">🇨🇦 Canada</option>
            <option value="MX">🇲🇽 Mexico</option>
            <option value="BR">🇧🇷 Brazil</option>
          </optgroup>
          <optgroup label="Middle East & Asia">
            <option value="AE">🇦🇪 UAE</option>
            <option value="SA">🇸🇦 Saudi Arabia</option>
            <option value="IN">🇮🇳 India</option>
            <option value="SG">🇸🇬 Singapore</option>
            <option value="AU">🇦🇺 Australia</option>
          </optgroup>
        </select>
      </div>
    </div>
  </>
);

// Checkout Form Component
const CheckoutForm = ({ customerInfo, onChange, onSubmit, loading, total, paymentGateway, selectedShipping, onSelectShipping, shippingCost, taxInfo }) => (
  <Card className="sticky top-24">
    <CardHeader>
      <CardTitle style={{ fontFamily: 'Archivo, sans-serif' }}>Order Summary</CardTitle>
    </CardHeader>
    <CardContent className="space-y-6">
      <OrderSummary total={total} shippingCost={shippingCost} taxInfo={taxInfo} />
      <Separator />
      
      <form onSubmit={onSubmit} className="space-y-4">
        <CustomerFormFields customerInfo={customerInfo} onChange={onChange} />
        
        {/* Courier Selection - Go-Dap */}
        {customerInfo.province && customerInfo.country && (
          <>
            <Separator />
            <CourierSelection
              destState={customerInfo.province || customerInfo.city}
              countryCode={customerInfo.country}
              selectedRate={selectedShipping}
              onSelectRate={onSelectShipping}
            />
          </>
        )}
        
        {/* Payment Gateway Indicator */}
        {customerInfo.country && (
          <div className="flex items-center justify-center gap-2 py-2 px-3 rounded-lg bg-muted/50">
            {paymentGateway === 'paystack' ? (
              <>
                <Banknote className="w-4 h-4 text-green-600" />
                <span className="text-sm text-muted-foreground">Payment via <strong className="text-green-600">Paystack</strong></span>
              </>
            ) : (
              <>
                <CreditCard className="w-4 h-4 text-blue-600" />
                <span className="text-sm text-muted-foreground">Payment via <strong className="text-blue-600">Stripe</strong></span>
              </>
            )}
          </div>
        )}
        
        <Button type="submit" size="lg" className="w-full gap-2" disabled={loading || (customerInfo.country && !selectedShipping)} data-testid="checkout-btn">
          {loading ? 'Processing...' : 'Proceed to Checkout'}
          <ArrowRight className="w-4 h-4" />
        </Button>
      </form>
      
      <p className="text-xs text-center text-muted-foreground">
        Secure checkout powered by {paymentGateway === 'paystack' ? 'Paystack' : 'Stripe'}
      </p>
    </CardContent>
  </Card>
);

// Custom hook for checkout logic
const useCheckout = (sessionId, cartTotal) => {
  const [loading, setLoading] = useState(false);
  const [selectedShipping, setSelectedShipping] = useState(null);
  const [taxInfo, setTaxInfo] = useState(null);
  const [customerInfo, setCustomerInfo] = useState({ 
    email: '', 
    name: '', 
    phone: '',
    address: '',
    city: '',
    province: '',
    postalCode: '',
    country: ''
  });

  // Calculate shipping cost in USD (approximate conversion for checkout)
  const shippingCostUSD = useMemo(() => {
    if (!selectedShipping) return 0;
    const rate = selectedShipping.rate || 0;
    const currency = selectedShipping.currency || 'USD';
    // Approximate conversions to USD
    const conversionRates = {
      'USD': 1, 'NGN': 0.00065, 'GHS': 0.08, 'KES': 0.0065,
      'ZAR': 0.055, 'GBP': 1.27, 'EUR': 1.09
    };
    return rate * (conversionRates[currency] || 1);
  }, [selectedShipping]);

  // Fetch tax info when country/state changes
  useEffect(() => {
    const fetchTax = async () => {
      if (!customerInfo.country || cartTotal <= 0) {
        setTaxInfo(null);
        return;
      }
      
      try {
        const params = new URLSearchParams({
          subtotal: cartTotal.toString(),
          country_code: customerInfo.country,
          shipping: shippingCostUSD.toString()
        });
        if (customerInfo.province) {
          params.append('state_code', customerInfo.province);
        }
        
        const response = await axios.get(`${API}/tax/calculate/quick?${params}`);
        setTaxInfo(response.data);
      } catch (err) {
        console.error('Tax calculation error:', err);
        setTaxInfo(null);
      }
    };
    
    fetchTax();
  }, [customerInfo.country, customerInfo.province, cartTotal, shippingCostUSD]);

  // Determine payment gateway based on country
  const paymentGateway = useMemo(() => {
    return PAYSTACK_COUNTRIES.includes(customerInfo.country) ? 'paystack' : 'stripe';
  }, [customerInfo.country]);

  const handleCustomerInfoChange = useCallback((field, value) => {
    setCustomerInfo(prev => ({ ...prev, [field]: value }));
    // Reset shipping when address changes
    if (['province', 'country', 'city'].includes(field)) {
      setSelectedShipping(null);
    }
  }, []);

  const handleSelectShipping = useCallback((rate) => {
    setSelectedShipping(rate);
  }, []);

  const processCheckout = useCallback(async () => {
    if (!customerInfo.email || !customerInfo.name || !customerInfo.phone) {
      toast.error('Please fill in email, name, and phone number');
      return false;
    }
    if (!customerInfo.address || !customerInfo.city || !customerInfo.postalCode || !customerInfo.country) {
      toast.error('Please fill in complete shipping address');
      return false;
    }
    if (!selectedShipping) {
      toast.error('Please select a delivery option');
      return false;
    }
    
    // Build structured shipping address for Alibaba
    const structuredAddress = `${customerInfo.name}, ${customerInfo.address}, ${customerInfo.city}, ${customerInfo.province || ''}, ${customerInfo.country}, ${customerInfo.postalCode}`;
    
    setLoading(true);
    try {
      // Check if we should use Paystack (for Nigeria, Ghana, South Africa, Kenya)
      if (PAYSTACK_COUNTRIES.includes(customerInfo.country)) {
        // Use Paystack for African countries
        const currencyMap = {
          'NG': 'NGN',
          'GH': 'GHS',
          'ZA': 'ZAR',
          'KE': 'KES'
        };
        const currency = currencyMap[customerInfo.country] || 'NGN';
        
        // Calculate total with shipping
        const totalWithShipping = cartTotal + shippingCostUSD;
        
        // First create the order
        const orderResponse = await axios.post(`${API}/orders`, {
          cart_id: sessionId,
          customer_email: customerInfo.email,
          customer_name: customerInfo.name,
          customer_phone: customerInfo.phone,
          shipping_address: structuredAddress,
          shipping_details: {
            full_name: customerInfo.name,
            mobile: customerInfo.phone,
            address: customerInfo.address,
            city: customerInfo.city,
            province: customerInfo.province,
            country: customerInfo.country,
            zip: customerInfo.postalCode
          },
          courier_info: selectedShipping ? {
            courier: selectedShipping.courier,
            courier_name: selectedShipping.courier_name,
            service_type: selectedShipping.service_type,
            rate: selectedShipping.rate,
            currency: selectedShipping.currency,
            estimated_days: selectedShipping.estimated_days
          } : null,
          payment_method: 'paystack'
        });
        
        const orderId = orderResponse.data.order_id;
        
        // Initialize Paystack payment (include shipping cost)
        const paystackResponse = await axios.post(`${API}/paystack/initialize`, {
          email: customerInfo.email,
          amount: totalWithShipping,
          currency: currency,
          callback_url: `${window.location.origin}/checkout/success?gateway=paystack&reference=`,
          order_id: orderId,
          metadata: {
            customer_name: customerInfo.name,
            customer_phone: customerInfo.phone,
            shipping_address: structuredAddress,
            shipping_option: selectedShipping?.service_type
          }
        });
        
        if (paystackResponse.data.success) {
          // Redirect to Paystack checkout
          window.location.href = paystackResponse.data.authorization_url;
          return true;
        } else {
          toast.error('Failed to initialize Paystack payment');
          return false;
        }
      } else {
        // Use Stripe for other countries
        const totalWithShipping = cartTotal + shippingCostUSD;
        
        const response = await axios.post(`${API}/checkout`, {
          cart_id: sessionId,
          customer_email: customerInfo.email,
          customer_name: customerInfo.name,
          customer_phone: customerInfo.phone,
          shipping_address: structuredAddress,
          shipping_details: {
            full_name: customerInfo.name,
            mobile: customerInfo.phone,
            address: customerInfo.address,
            city: customerInfo.city,
            province: customerInfo.province,
            country: customerInfo.country,
            zip: customerInfo.postalCode
          },
          courier_info: selectedShipping ? {
            courier: selectedShipping.courier,
            courier_name: selectedShipping.courier_name,
            service_type: selectedShipping.service_type,
            rate: selectedShipping.rate,
            currency: selectedShipping.currency,
            estimated_days: selectedShipping.estimated_days
          } : null,
          shipping_cost: shippingCostUSD,
          origin_url: window.location.origin
        });
        
        window.location.href = response.data.checkout_url;
        return true;
      }
    } catch (err) {
      console.error('Checkout error:', err);
      toast.error(err.response?.data?.detail || 'Failed to process checkout');
      return false;
    } finally {
      setLoading(false);
    }
  }, [customerInfo, sessionId, cartTotal, selectedShipping, shippingCostUSD, taxInfo]);

  return { loading, customerInfo, handleCustomerInfoChange, processCheckout, paymentGateway, selectedShipping, handleSelectShipping, shippingCostUSD, taxInfo };
};

const CartPage = () => {
  const { cart, updateQuantity, removeFromCart, getCartTotal, sessionId, loading: cartLoading, getItemKey } = useCart();
  const cartTotal = useMemo(() => getCartTotal(), [getCartTotal]);
  const { 
    loading: checkoutLoading, 
    customerInfo, 
    handleCustomerInfoChange, 
    processCheckout, 
    paymentGateway,
    selectedShipping,
    handleSelectShipping,
    shippingCostUSD,
    taxInfo
  } = useCheckout(sessionId, cartTotal);

  const handleQuantityChange = useCallback(async (itemKey, newQuantity) => {
    if (newQuantity < 1) {
      await removeFromCart(itemKey);
      toast.success('Item removed from cart');
    } else {
      await updateQuantity(itemKey, newQuantity);
    }
  }, [removeFromCart, updateQuantity]);

  const handleRemoveItem = useCallback(async (itemKey) => {
    await removeFromCart(itemKey);
    toast.success('Item removed from cart');
  }, [removeFromCart]);

  const handleCheckout = useCallback(async (e) => {
    e.preventDefault();
    await processCheckout();
  }, [processCheckout]);

  const isEmpty = useMemo(() => !cart.items || cart.items.length === 0, [cart.items]);

  if (isEmpty) return <EmptyCart />;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-slide-up">
      <h1 className="text-2xl sm:text-3xl font-bold mb-8" style={{ fontFamily: 'Archivo, sans-serif' }}>
        Shopping Cart
      </h1>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {cart.items.map((item) => {
            const itemKey = getItemKey(item);
            return (
              <CartItem 
                key={itemKey}
                item={item}
                itemKey={itemKey}
                onQuantityChange={handleQuantityChange}
                onRemove={handleRemoveItem}
                loading={cartLoading}
              />
            );
          })}
          
          <div className="pt-4">
            <Link to="/products" data-testid="continue-shopping">
              <Button variant="outline" className="gap-2">
                <Package className="w-4 h-4" />
                Continue Shopping
              </Button>
            </Link>
          </div>
        </div>

        <div className="lg:col-span-1">
          <CheckoutForm 
            customerInfo={customerInfo}
            onChange={handleCustomerInfoChange}
            onSubmit={handleCheckout}
            loading={checkoutLoading}
            total={cartTotal}
            paymentGateway={paymentGateway}
            selectedShipping={selectedShipping}
            onSelectShipping={handleSelectShipping}
            shippingCost={shippingCostUSD}
            taxInfo={taxInfo}
          />
        </div>
      </div>
    </div>
  );
};

export default CartPage;
